import type { RoomsParticipantDistribution } from '@/domain/shared/types'
import { useIntl } from '@/intl'
import * as ParticipantsFilterPrimitive from '@/scopes/common/components/Filters/Participants'
import { useParticipantsFilter } from '@/scopes/common/components/Filters/Participants/hooks'
import {
  ChildInfo,
  ChildWithoutAdultRoomInfo,
  GroupLink,
} from '@/scopes/common/components/Filters/Participants/ParticipantsBanner'
import { useDependencies } from '@/scopes/common/infrastructure/dependencies'
import * as DialogPrimitive from '@/scopes/search/components/QuickSearchBarLegacy/DialogPrimitive'
import { useQuickSearchStore } from '@/scopes/search/components/QuickSearchBarLegacy/store'
import {
  Button,
  ButtonLoaderIcon,
  Icon,
  SheetClose,
  SheetContentWrapper,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  Typography,
} from '@sunwebgroup/ui-components'

import * as React from 'react'
import { styled } from 'styled-components'
import { Item } from './QuickSearchBarLegacy'

const SheetBody = styled(ParticipantsFilterPrimitive.Body)`
  width: 100%;
`

const SheetFooterStyled = styled(SheetFooter)`
  bottom: 0;
`
/**
 * @deprecated Use ParticipantsFilterComponent instead.
 */
export function ParticipantsFilterComponentLegacy({
  disabled,
  setSelection,
  defaultDistribution,
}: {
  setSelection: (selection: RoomsParticipantDistribution) => void
  disabled?: boolean
  defaultDistribution?: RoomsParticipantDistribution
}) {
  const intl = useIntl()
  const { setActiveItem } = useQuickSearchStore()

  const {
    handleConfirm: handleConfirmFilter,
    getFormattedText,
    showParticipants,
    showChildren,
    warningNoBabiesWithoutAdults,
  } = useParticipantsFilter({
    isFetching: disabled ?? false,
    setSelection,
  })
  const disabledSaveAction = warningNoBabiesWithoutAdults
  const disabledCloseButton = disabled || disabledSaveAction
  const { roomsParticipantsDistributionStore } = useDependencies()

  React.useEffect(() => {
    if (!defaultDistribution)
      return
    roomsParticipantsDistributionStore.setState({
      roomsParticipantDistribution: defaultDistribution,
    })
  }, [defaultDistribution])

  function handleConfirm() {
    setActiveItem(null)
    if (disabledSaveAction)
      return
    handleConfirmFilter()
  }

  return (
    <Item.Root
      id="participants-filter"
      icon="IconGroup"
      title={intl.formatMessage({
        id: 'quicksearch_filter_participants_title',
      })}
      onClose={handleConfirm}
    >
      <Item.AsPopover
        selectionFormattedText={getFormattedText().desktop}
        disabled={disabled}
        data-qa="participants-filter"
      >
        {(showChildren || showParticipants || warningNoBabiesWithoutAdults) && (
          <ParticipantsFilterPrimitive.BannerWrapper>
            {showChildren && !warningNoBabiesWithoutAdults && <ChildInfo />}
            {warningNoBabiesWithoutAdults && <ChildWithoutAdultRoomInfo />}
            {showParticipants && <GroupLink />}
          </ParticipantsFilterPrimitive.BannerWrapper>
        )}
        <ParticipantsFilterPrimitive.Body />
        <ParticipantsFilterPrimitive.Footer>
          <ParticipantsFilterPrimitive.CloseButton $fullSize onClick={handleConfirm} disabled={disabledCloseButton}>
            {intl.formatMessage({
              id: 'quicksearch_save_button_text',
            })}
          </ParticipantsFilterPrimitive.CloseButton>
        </ParticipantsFilterPrimitive.Footer>
      </Item.AsPopover>
      <Item.AsSheet
        selectionFormattedText={getFormattedText().desktop}
        disabled={disabled}
        data-qa="participants-filter"
      >
        <>
          <SheetHeader>
            <SheetTitle>{intl.formatMessage({ id: 'quicksearch_filter_participants_title' })}</SheetTitle>
            <SheetClose>{intl.formatMessage({ id: 'quicksearch_cancel_button_text' })}</SheetClose>
          </SheetHeader>
          {(showChildren || showParticipants || warningNoBabiesWithoutAdults) && (
            <ParticipantsFilterPrimitive.BannerWrapper $isSheet>
              {showChildren && !warningNoBabiesWithoutAdults && <ChildInfo />}
              {warningNoBabiesWithoutAdults && <ChildWithoutAdultRoomInfo />}
              {showParticipants && <GroupLink />}
            </ParticipantsFilterPrimitive.BannerWrapper>
          )}
          <SheetContentWrapper>
            <SheetBody />
          </SheetContentWrapper>
          <SheetFooterStyled $isFixed={false}>
            <ParticipantsFilterPrimitive.CloseButton $fullSize onClick={handleConfirm} disabled={disabledCloseButton}>
              {intl.formatMessage({
                id: 'quicksearch_save_button_text',
              })}
            </ParticipantsFilterPrimitive.CloseButton>
          </SheetFooterStyled>
        </>
      </Item.AsSheet>
      <Item.AsDialog
        selectionFormattedText={getFormattedText().desktop}
        disabled={disabled}
        data-qa="participants-filter"
      >
        <DialogPrimitive.Root>
          <DialogPrimitive.Header>
            <DialogPrimitive.HeaderTitle>
              <Typography $variant="body-lg-bold">
                {intl.formatMessage({ id: 'quicksearch_filter_participants_title' })}
              </Typography>
              <Button onClick={handleConfirm} $hierarchy="tertiary" $withoutColor>
                <Icon glyph="IconCrossMedium" size="md" />
              </Button>
            </DialogPrimitive.HeaderTitle>
          </DialogPrimitive.Header>
          {(showChildren || showParticipants || warningNoBabiesWithoutAdults) && (
            <ParticipantsFilterPrimitive.DialogBannerWrapper>
              {showChildren && !warningNoBabiesWithoutAdults && <ChildInfo />}
              {warningNoBabiesWithoutAdults && <ChildWithoutAdultRoomInfo />}
              {showParticipants && <GroupLink />}
            </ParticipantsFilterPrimitive.DialogBannerWrapper>
          )}
          <DialogPrimitive.Body>
            <ParticipantsFilterPrimitive.Body />
          </DialogPrimitive.Body>
          <DialogPrimitive.Footer>
            <Button $fullSize onClick={handleConfirm} disabled={disabledCloseButton}>
              {disabled && <ButtonLoaderIcon />}
              {intl.formatMessage({
                id: 'quicksearch_save_button_text',
              })}
            </Button>
          </DialogPrimitive.Footer>
        </DialogPrimitive.Root>
      </Item.AsDialog>
      <Item.AsIcon
        selectionFormattedText={getFormattedText().tablet}
        compactSelectionFormattedText={getFormattedText().mobile}
      />
    </Item.Root>
  )
}
